import { AppRoute } from 'app/app.route.const';
import { PrimaryButton } from 'components/buttons/primary-button/primary.button';
import { Text } from 'components/text/Text';
import { AccountRight, SubscriptionState } from 'logic/api-models/api-models';
import {
  getAccountRights,
  getIsAuthenticated,
} from 'logic/store/authentication/authentication.selectors';
import {
  getAfternoonCarePricing,
  getElitePaymentSeekerInfo,
} from 'logic/store/elitetutor/elitetutor.selectors';
import { elitetutorSlice } from 'logic/store/elitetutor/elitetutor.slice';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { translate, translationKeys } from 'logic/translations/translations.service';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import { Breakpoints } from 'logic/hooks/use-breakpoints';
import { ViewAs } from '../up-coming-lessons/UpComingLessons.types';

const Banner = styled.section`
  width: 100%;
  background-color: ${colors.afternoonCareBanner};
  height: 50px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: nowrap;

  @media (${Breakpoints.maxTablet}) {
    height: auto;
    min-height: 50px;
    padding: 8px 12px;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  @media (${Breakpoints.maxTablet}) {
    width: 100%;
  }
`;

const ActionButton = styled(PrimaryButton)`
  && {
    background-color: ${colors.white};
    border-color: ${colors.white};
    color: ${colors.primary};
    height: 34px;
    padding: 0 14px;
    line-height: 32px;
    font-size: 14px;
  }

  &&:hover,
  &&:focus,
  &&:active {
    background-color: ${colors.white};
    border-color: ${colors.white};
    color: ${colors.primary};
    opacity: 0.92;
  }

  @media (${Breakpoints.maxTablet}) {
    height: 30px;
    line-height: 28px;
    font-size: 13px;
    padding: 0 10px;
  }
`;

const PROFILE_ROUTE_PREFIXES = [
  AppRoute.UserProfile,
  generatePath(AppRoute.UnitOverview, { viewAs: ViewAs.SEEKER }),
  AppRoute.Billing,
  AppRoute.MyPricing,
  AppRoute.MyPayment,
  AppRoute.AfternoonCare,
  AppRoute.AfternoonCareSuccess,
  AppRoute.PersonalizedSubscription,
  AppRoute.AfternoonCareUpsell,
];

const normalizeRoutePrefix = (route: string) => route.split('/:')[0];

const isProfilePath = (pathname?: string) => {
  if (!pathname) return false;
  return PROFILE_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(normalizeRoutePrefix(prefix)));
};

export const AfternoonCarePromotionBar: FC = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const accountRights = useSelector(getAccountRights);
  const seekerInfo = useSelector(getElitePaymentSeekerInfo);
  const { isAvailable: isPricingAvailable, discountPercentage } = useSelector(getAfternoonCarePricing);

  const isEliteSeeker = !!accountRights?.includes(AccountRight.ELITESEEKER);
  const inProfileArea = isProfilePath(pathname);
  const hasSeekerInfo = seekerInfo != null;
  const hasActiveSubscription = seekerInfo?.subscription?.state === SubscriptionState.active;
  const hasAfternoonCare = (seekerInfo?.subscription?.afternoonCareAddon?.quantity ?? 0) > 0;

  useEffect(() => {
    if (isAuthenticated && inProfileArea && isEliteSeeker) {
      dispatch(elitetutorSlice.actions.getElitePaymentSeekerInfo());
      dispatch(elitetutorSlice.actions.getElitePaymentConfig());
    }
  }, [dispatch, inProfileArea, isAuthenticated, isEliteSeeker]);

  if (
    !isAuthenticated ||
    !inProfileArea ||
    !isEliteSeeker ||
    !hasSeekerInfo ||
    !hasActiveSubscription ||
    hasAfternoonCare ||
    !isPricingAvailable
  ) {
    return null;
  }

  return (
    <Banner>
      <TextWrapper>
        <Text size={14} color="white" fontWeight={400}>
          <span style={{ fontWeight: 700 }}>
            {translate(translationKeys.afternoonCareUpsell.promotionBar.textBold)}
          </span>{' '}
          {translate(translationKeys.afternoonCareUpsell.promotionBar.textRegular, {
            discount: discountPercentage,
          })}
        </Text>
      </TextWrapper>
      <ActionButton
        onClick={() =>
          dispatch(navigationSlice.actions.navigateTo({ path: AppRoute.AfternoonCare }))
        }
      >
        {translate(translationKeys.afternoonCareUpsell.promotionBar.button)}
      </ActionButton>
    </Banner>
  );
};
